/**
 * Users Table Component
 *
 * Main table component displaying all users
 */

import React from 'react';
import type { User } from '../types/user';
import { __ } from '@/hub-shared/utils/i18n';
import UserRow from './UserRow';
import { LoadingState, TablePagination } from '@tpct/admin-ui';
import UsersEmptyState from './UsersEmptyState';

type UsersTableProps = {
  users: User[];
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  onEdit: (user: User) => void;
  onDelete: (userId: number) => void;
  currentUser: {
    id: number;
    roles: string[];
  };
  canManageUsers?: boolean;
};

const UsersTable = ({
  users,
  loading,
  page,
  totalPages,
  onPageChange,
  onRefresh,
  onEdit,
  onDelete,
  currentUser,
  canManageUsers = true,
}: UsersTableProps) => {
  if (loading) {
    return (
      <LoadingState
        columns={[__('Nom'), __('Rôle'), __('Dossiers clients'), __('Actions')]}
        className="users-loading"
      />
    );
  }

  if (users.length === 0) {
    return <UsersEmptyState onRefresh={onRefresh} />;
  }

  return (
    <div className="container-style">
      <table className="thn-table users-table">
        <thead>
          <tr>
            <th scope="col">{__('Nom')}</th>
            <th scope="col">{__('Rôle')}</th>
            <th scope="col">{__('Dossiers clients')}</th>
            <th scope="col">{__('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              onEdit={onEdit}
              onDelete={onDelete}
              currentUser={currentUser}
              canManageUsers={canManageUsers}
            />
          ))}
        </tbody>
      </table>
      <TablePagination
        page={page - 1}
        totalPages={totalPages}
        onPrev={() => onPageChange(Math.max(1, page - 1))}
        onNext={() => onPageChange(Math.min(totalPages, page + 1))}
        previousPageAriaLabel={__('Page précédente')}
        nextPageAriaLabel={__('Page suivante')}
      />
    </div>
  );
};

export default UsersTable;

